import { Box, Typography, CircularProgress } from '@mui/material'
import type { TClientRequest, TFile } from '@modules/types'
import { FilesSection } from '../common/FilesSection'
import { SongsSection } from '../common/SongsSection'
import { CommentsSection } from '../common/CommentsSection'
import { ComponentsSection } from './ComponentsSection'
import { sectionDividerSx } from '../styles'

interface PublicationDetailsExpandedProps {
  expandedData: TClientRequest | undefined
  files?: TFile[]
  loading: boolean
}

export const PublicationDetailsExpanded = ({
  expandedData,
  files,
  loading,
}: PublicationDetailsExpandedProps) => {
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
        <CircularProgress sx={{ width: '100%' }} />
      </Box>
    )
  }

  if (!expandedData) {
    return (
      <Box sx={{ textAlign: 'center', py: 3 }}>
        <Typography variant="body2" color="text.secondary">
          Error loading details
        </Typography>
      </Box>
    )
  }

  const { additionalComments, songs, distributionDetails, id: requestId } = expandedData

  const hasFiles = files && files.length > 0
  const hasSongs = songs && songs.length > 0
  const hasComponents = distributionDetails && distributionDetails.length > 0
  const hasComments = Boolean(additionalComments)

  const sectionsCount = [hasFiles, hasSongs, hasComponents, hasComments].filter(Boolean).length

  return (
    <>
      {hasFiles && <FilesSection files={files} requestId={requestId} />}
      {hasFiles && (hasSongs || hasComponents || hasComments) && <Box sx={sectionDividerSx} />}

      {hasSongs && <SongsSection songs={songs} />}
      {hasSongs && (hasComponents || hasComments) && <Box sx={sectionDividerSx} />}

      {hasComponents && <ComponentsSection lines={distributionDetails} />}
      {hasComponents && hasComments && <Box sx={sectionDividerSx} />}

      {hasComments && <CommentsSection comments={additionalComments} />}

      {sectionsCount === 0 && (
        <Box sx={{ textAlign: 'center', py: 3 }}>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
            No additional details available
          </Typography>
        </Box>
      )}
    </>
  )
}
